import { FC, PropsWithChildren } from 'react'
import { useShallow } from 'zustand/react/shallow'

import { Grid, GridProps, useTheme } from '@mui/material'
import { useHistoricalStatisticsStore } from '@/stores/historicalStatistics'

import { ComponentWithStatisticsQueryParams } from '../cards/types'

type ContainerProps = PropsWithChildren & GridProps

const Container: FC<ContainerProps> = ({ children, ...props }) => {
  const theme = useTheme()

  return (
    <Grid
      p={2}
      pb={0}
      size={6}
      {...props}
      sx={{
        backgroundColor: theme.palette.statistics.panel.backgroundColor,
        ...props.sx
      }}
    >
      {children}
    </Grid>
  )
}

type StatisticsPanelGridRowProps = {
  component: FC<ComponentWithStatisticsQueryParams>
  GridProps?: GridProps
}

/** StatisticsPanelGridRow
 * is a reusable component that renders a row in the statistics panel.
 * It accepts a component to render and optional Grid properties.
 * It uses the historical statistics store to determine the query parameters and whether to render head-to-head (H2H) statistics.
 *
 * @param {FC<ComponentWithStatisticsQueryParams>} component - The component to render in the row.
 * @param {GridProps} [GridProps] - Optional properties for the Grid component.
 * @returns {JSX.Element}
 * ```jsx
 * <>
 *   <Grid {...GridProps}>
 *     <Component queryParams={main} />
 *   </Grid>
 *
 *   {isH2H && (
 *     <Grid {...GridProps}>
 *       <Component queryParams={h2h} />
 *     </Grid>
 *   )}
 * </>
 * ```
 */

export const StatisticsPanelGridRow: FC<StatisticsPanelGridRowProps> = ({
  component: Component,
  GridProps
}) => {
  const { queryParams, isH2H } = useHistoricalStatisticsStore(
    useShallow((state) => ({
      queryParams: state.queryParams,
      isH2H: state.isH2H
    }))
  )

  const { main, h2h } = queryParams

  return (
    <>
      <Container {...GridProps}>
        <Component queryParams={main} />
      </Container>

      {isH2H && (
        <Container {...GridProps}>
          <Component queryParams={h2h} />
        </Container>
      )}
    </>
  )
}
